import { Component, OnInit, Input, OnChanges, SimpleChanges , DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { DatePicker } from 'primeng/datepicker';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DecisionLogItemService, DecisionLogItem, CreateDecisionLogItemDto } from '../../services/decision-log-item.service';

@Component({
  selector: 'app-decision-log',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Tooltip,
    Dialog,
    InputText,
    DatePicker,
    ConfirmDialog,
  ],
  templateUrl: './decision-log.component.html',
  styleUrl: './decision-log.component.scss',
  providers: [MessageService, ConfirmationService]
})
export class DecisionLogComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  @Input() projectId: string = '';

  items: DecisionLogItem[] = [];
  selectedItem: DecisionLogItem | null = null;
  displayItemDialog: boolean = false;
  isEditItemMode: boolean = false;
  decisionDate: Date | null = null;
  loading: boolean = false;
  fieldTouched: Record<string, boolean> = {};

  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private decisionLogItemService: DecisionLogItemService
  ) {}

  ngOnInit() {
    if (this.projectId) {
      this.loadItems();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['projectId'] && this.projectId) {
      this.loadItems();
    }
  }

  loadItems() {
    if (!this.projectId) {
      return;
    }
    this.loading = true;
    this.decisionLogItemService.getByProjectId(this.projectId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (items) => {
        this.items = items;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading decision log items:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load decision log items. Please try again.'
        });
        this.loading = false;
      }
    });
  }

  showAddItemDialog() {
    if (!this.projectId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Project ID is required to add decision log items.'
      });
      return;
    }
    const todayStr = this.formatDateToLocalString(new Date());
    this.selectedItem = {
      id: '',
      projectId: this.projectId,
      number: this.generateNumber(),
      date: todayStr,
      decisionTaken: '',
      owner: '',
      comments: ''
    };
    this.decisionDate = new Date();
    this.fieldTouched = {};
    this.isEditItemMode = false;
    this.displayItemDialog = true;
  }

  showEditItemDialog(item: DecisionLogItem) {
    this.selectedItem = { ...item };
    this.decisionDate = item.date ? this.parseLocalDateString(item.date) : new Date();
    this.fieldTouched = {};
    this.isEditItemMode = true;
    this.displayItemDialog = true;
  }

  resetItemForm() {
    this.fieldTouched = {};
    if (!this.selectedItem) return;
    if (this.isEditItemMode) {
      const item = this.items.find(i => i.id === this.selectedItem?.id);
      if (item) {
        this.selectedItem = { ...item };
        this.decisionDate = item.date ? this.parseLocalDateString(item.date) : new Date();
      }
    } else {
      this.selectedItem.number = String(this.items.length + 1);
      this.selectedItem.decisionTaken = '';
      this.selectedItem.owner = '';
      this.selectedItem.comments = '';
      this.selectedItem.date = '';
      this.decisionDate = new Date();
    }
  }

  saveItem() {
    this.fieldTouched['decisionTaken'] = true;
    this.fieldTouched['owner'] = true;

    if (!this.selectedItem || !this.selectedItem.decisionTaken.trim() || !this.selectedItem.owner.trim()) {
      return;
    }

    if (!this.projectId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Project ID is required.'
      });
      return;
    }

    if (this.decisionDate) {
      this.selectedItem.date = this.formatDateToLocalString(this.decisionDate);
    }

    if (this.isEditItemMode) {
      // Update existing item
      this.decisionLogItemService.updateItem(this.selectedItem.id, this.selectedItem).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (updatedItem) => {
          const index = this.items.findIndex(i => i.id === updatedItem.id);
          if (index !== -1) {
            this.items[index] = updatedItem;
          }
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Decision log item updated successfully.'
          });
          this.displayItemDialog = false;
          this.selectedItem = null;
        },
        error: (error) => {
          console.error('Error updating decision log item:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update decision log item. Please try again.'
          });
        }
      });
    } else {
      // Create new item
      const createDto: CreateDecisionLogItemDto = {
        projectId: this.projectId,
        number: this.selectedItem.number,
        date: this.selectedItem.date,
        decisionTaken: this.selectedItem.decisionTaken,
        owner: this.selectedItem.owner,
        comments: this.selectedItem.comments
      };

      this.decisionLogItemService.addItem(createDto).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (newItem) => {
          this.items.push(newItem);
          this.items.sort((a, b) => {
            const numA = parseInt(a.number.replace(/\D/g, '')) || 0;
            const numB = parseInt(b.number.replace(/\D/g, '')) || 0;
            return numA - numB;
          });
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Decision log item added successfully.'
          });
          this.displayItemDialog = false;
          this.selectedItem = null;
        },
        error: (error) => {
          console.error('Error adding decision log item:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add decision log item. Please try again.'
          });
        }
      });
    }
  }

  deleteItem(item: DecisionLogItem) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${item.decisionTaken || ''}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.decisionLogItemService.deleteItem(item.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            const index = this.items.findIndex(i => i.id === item.id);
            if (index !== -1) {
              this.items.splice(index, 1);
            }
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Decision log item deleted successfully.'
            });
          },
          error: (error) => {
            console.error('Error deleting decision log item:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete decision log item. Please try again.'
            });
          }
        });
      }
    });
  }

  generateNumber(): string {
    if (this.items.length === 0) {
      return '01';
    }
    const maxNum = Math.max(...this.items.map(i => {
      const num = parseInt(i.number.replace(/\D/g, ''));
      return isNaN(num) ? 0 : num;
    }));
    return String(maxNum + 1).padStart(2, '0');
  }

  /** Parse YYYY-MM-DD as local date (avoids timezone off-by-one). */
  private parseLocalDateString(dateStr: string): Date | null {
    const parts = dateStr.trim().split('-');
    if (parts.length !== 3) return null;
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
    const date = new Date(y, m, d);
    if (isNaN(date.getTime())) return null;
    return date;
  }

  /** Format Date to YYYY-MM-DD using local date. */
  private formatDateToLocalString(date: Date): string {
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '-';
    const date = this.parseLocalDateString(dateString);
    if (!date) return '-';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }
}

